import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NotificationService } from '../../../../core/_infrastructure/classes/services/notification.service';
import { ListBase } from 'src/app/components/core/_infrastructure/classes/listBase';
import { Table } from 'primeng/table';
import {
    FilterSearchingSolicitudCam,
    SolicitudCamItemResultsViewModel,
    SolicitudCamItemResultViewModel,
    SolicitudCamItemViewModel,
    UserDataViewModel,
} from '../buscador-solicitud-cam.types';
import { FilterSearchingBaseViewModel } from 'src/app/components/core/_infrastructure/types/common';
import { proxiedPropertiesOf } from 'src/app/components/core/_infrastructure/types/utils';
import { EnumCodigosTwTcAcciones } from 'src/app/components/_importacion/solicitudes/types/enum';

@Component({
    selector: 'app-buscador-solicitudcam-list',
    templateUrl: './buscador-solicitudcam-list.component.html',
})
export class BuscadorSolicitudcamListComponent extends ListBase implements OnInit, OnChanges {
    @Input() result: SolicitudCamItemResultsViewModel;
    @Input() lastFilter: FilterSearchingSolicitudCam;
    @Input() userData: UserDataViewModel;
    @Output() search = new EventEmitter<FilterSearchingBaseViewModel>();
    @Output() edit = new EventEmitter<number | null>();
    @Output() view = new EventEmitter<number | null>();
    @Output() delete = new EventEmitter<{ idSolicitudCam: number; idTipoSolicitudCam: number }>();

    @ViewChild('tableSearch') turboTable: Table;

    cols: any[];

    /*
     * @scalero: Mantener éste código porque sirve de ejemplo para "contar" las instancias creadas de un mismo componente.
     * @date: 2023/09/12
     */
    /*
    static counter = 0;
    myCounter: number;
    */

    constructor(protected notificacionService: NotificationService) {
        super(notificacionService);
        // @scalero: Mantener éste código porque sirve de ejemplo para "contar" las instancias creadas de un mismo componente
        // BuscadorSolicitudcamListComponent.counter++;
    }

    ngOnInit() {
        this.initCols();
        this.loading = false;
        // @scalero: Mantener éste código porque sirve de ejemplo para "contar" las instancias creadas de un mismo componente
        // this.myCounter = Number(BuscadorSolicitudcamListComponent.counter);
    }

    private initCols(): void {
        const solicitudCampania = proxiedPropertiesOf<SolicitudCamItemViewModel>();

        this.cols = [
            { field: 'Link', header: '', reduce: true },
            { field: 'Image', header: '', reduce: true },
            { field: solicitudCampania.Id?.toString(), header: 'Id', display: 'none' },
            { field: solicitudCampania.Codigo?.toString(), header: 'Nº Solicitud' },
            { field: solicitudCampania.Campania?.toString(), header: 'Campaña' },
            { field: solicitudCampania.Periodo?.toString(), header: 'Periodo' },
            { field: solicitudCampania.OperadorRazonSocial?.toString(), header: 'Operador' },
            { field: solicitudCampania.FechaCreacion?.toString(), header: 'Fecha de Creación' },
            { field: solicitudCampania.FechaSolicitud?.toString(), header: 'Fecha de Presentación' },
            { field: solicitudCampania.EntidadAuditora?.toString(), header: 'Entidad Auditora' },
            { field: solicitudCampania.DescripcionComunidad, header: 'Com. Aut.' },
            { field: solicitudCampania.TipoSolicitudCam, header: 'Tipo Solicitud' },
        ];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.result?.currentValue) {
            this.result = { ...changes.result.currentValue };
            this.loading = false;
            setTimeout(() => {
                if (this.turboTable) {
                    this.turboTable.sortOrder = this.lastFilter?.Asc;
                    this.turboTable.sortField = this.lastFilter?.OrderBy;
                    this.turboTable.first = this.lastFilter?.PageNumber * this.result?.PageSize;
                }
            }, 100);
        }
    }

    onSearchLazy(event: any): void {
        if (!event.rows || event.sortField == 'Link' || event.sortField == 'Image') {
            return;
        }

        let pageNumber = event.first / event.rows;
        this.loading = true;

        const f: FilterSearchingBaseViewModel = {
            PageNumber: pageNumber,
            Asc: event.sortOrder,
            OrderBy: event.sortField || '',
        };

        this.search.emit(f);
    }

    onEdit(item: SolicitudCamItemResultViewModel): void {
        this.edit.emit(item?.Id);
    }

    onView(item: SolicitudCamItemResultViewModel): void {
        this.view.emit(item?.Id);
    }

    onDelete(item: SolicitudCamItemResultViewModel): void {
        if (!item.Id) {
            return;
        }
        const obj = {
            idSolicitudCam: item?.Id,
            idTipoSolicitudCam: item?.IdTipoSolicitudCam,
        };
        this.delete.emit(obj);
    }

    /**
     * Este método indica si se puede editar la solicitud.
     * Se podrá editar por el usuario propietario o el admin, siempre que estén en un estado permitido.
     * @param item SolicitudCamItemResultViewModel
     * @return boolean
     */
    hasWriteAccess(item: SolicitudCamItemResultViewModel): boolean {
        if (!item.PeriodoPresentacionVigente) {
            return false;
        }
        const hasAccess =
            this.userData?.loggedInUser?.getAction(EnumCodigosTwTcAcciones.SAVE_DATOS_BASICOS_SOL_CAM, item.IdEstadoSolicitudCam).Editable || false;
        return (this.userData?.isAdmin || this.userData?.idUsuario === item?.IdUsuario) && hasAccess;
    }

    /***
     * Este método indica si se puede borrar la solicitud.
     * Se podrá eliminar por el usuario propietario, siempre que estén en un estado permitido.
     * @param item SolicitudCamItemResultViewModel
     * @return boolean
     */
    hasDeleteAccess(item: SolicitudCamItemResultViewModel): boolean {
        const hasAccess = this.userData?.loggedInUser?.getAction(EnumCodigosTwTcAcciones.REMOVE_SOL_CAM, item.IdEstadoSolicitudCam).Editable || false;
        return this.userData.idUsuario === item?.IdUsuario && hasAccess;
    }

    /***
     * Este método indica si se puede visualizar la solicitud.
     * @param item SolicitudCamItemResultViewModel
     * @return boolean
     */
    hasViewAccess(item: SolicitudCamItemResultViewModel): boolean {
        const hasAccess = this.userData?.loggedInUser?.getAction(EnumCodigosTwTcAcciones.MENU_DATOS_BAS_SOL_CAM, item.IdEstadoSolicitudCam).Visible;
        return hasAccess || false;
    }

    hasPeriodoFinalizado(item: SolicitudCamItemResultViewModel): boolean {
        if (item && item?.PeriodoPresentacionVigente) return false;
        return !item?.PeriodoVigente;
    }
}
